/**
 * Dashboard / attendance UI theme — aliases app brand (Patient indigo SoT).
 *
 * Used by UserActivity, EditAttendanceModal, DateTimePicker (shared with Payroll).
 * Color changes still flow from patientTheme via brandTheme.
 */
import { BRAND_THEME, BRAND_THEME_HEX } from "../../ui/brandTheme";

export const DASHBOARD_THEME = BRAND_THEME;
export const DASHBOARD_THEME_HEX = BRAND_THEME_HEX;

export const DASHBOARD_BTN = {
  soft: `inline-flex items-center justify-center gap-1 h-7 px-2.5 text-[11px] font-medium rounded-md border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${BRAND_THEME.button.soft}`,
  icon: "inline-flex items-center justify-center h-7 w-7 rounded-md border border-indigo-200 bg-white text-indigo-600 hover:bg-indigo-50 hover:border-indigo-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0",
  /** Session remark — kept for future SHOW_SESSION_REMARK_UI re-enable */
  iconRemark:
    "inline-flex items-center justify-center h-7 w-7 rounded-md border border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 hover:border-slate-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0",
  /** Edit clock in/out times — indigo clock icon */
  iconSession:
    "inline-flex items-center justify-center h-7 w-7 rounded-md border border-indigo-200 bg-indigo-50/80 text-indigo-700 hover:bg-indigo-100 hover:border-indigo-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0",
  /** Add session — compact icon-only */
  iconAdd:
    "inline-flex items-center justify-center h-7 w-7 rounded-md border border-indigo-200 bg-white text-indigo-600 hover:bg-indigo-50 hover:border-indigo-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0",
  modalPrimary: `inline-flex items-center justify-center gap-1.5 h-9 px-3 text-sm font-semibold rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${BRAND_THEME.button.primary}`,
  modalCancel:
    "inline-flex items-center justify-center gap-1.5 h-9 px-3 text-sm font-medium rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed",
};

export const DASHBOARD_FIELD = {
  label: "block text-[11px] font-medium text-slate-700 mb-1",
  input: `w-full h-9 px-3 pr-16 text-sm rounded-md border border-indigo-200 bg-white text-slate-800 outline-none transition-colors cursor-pointer hover:border-indigo-300 ${BRAND_THEME.fieldFocus}`,
  textarea: `w-full px-3 py-2 text-sm rounded-md border border-indigo-200 bg-white text-slate-800 outline-none transition-colors resize-none min-h-[5rem] placeholder:text-slate-400 ${BRAND_THEME.fieldFocus}`,
};

/** Time-card table — same 13px type and roomier rows on Dashboard + Payroll. */
export const DASHBOARD_TABLE = {
  headRow: BRAND_THEME.tableHead.row,
  headCell:
    "px-2.5 py-2.5 text-left text-[13px] font-semibold text-indigo-700 whitespace-nowrap",
  bodyCell: "px-2.5 py-2.5 align-middle text-[13px] text-slate-700",
  bodyCellTop: "px-2.5 py-2.5 align-top text-[13px] text-slate-700",
};

/**
 * Column width % for time-card table (must sum to 100 with action, or 100 without).
 * Remark/notes column is hidden from UI (`SHOW_SESSION_REMARK_UI`); widths redistributed.
 * `embedded` = Payroll modal (wider breaks + action).
 */
export function getTimeCardColWidths(embedded, hasAction) {
  if (embedded && hasAction) {
    return {
      date: "13%",
      session: "7%",
      clockIn: "14%",
      clockOut: "14%",
      breaks: "26%",
      work: "14%",
      action: "72px",
    };
  }
  if (embedded) {
    return {
      date: "14%",
      session: "8%",
      clockIn: "15%",
      clockOut: "15%",
      breaks: "28%",
      work: "20%",
    };
  }
  if (hasAction) {
    return {
      date: "13%",
      session: "7%",
      clockIn: "14%",
      clockOut: "14%",
      breaks: "26%",
      work: "14%",
      action: "72px",
    };
  }
  return {
    date: "14%",
    session: "8%",
    clockIn: "15%",
    clockOut: "15%",
    breaks: "28%",
    work: "20%",
  };
}

/** Modal shell — Patient AddPhysician / Documents pattern. */
export const DASHBOARD_MODAL = {
  backdrop:
    "fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40",
  panel:
    "w-full max-w-2xl rounded-xl border border-indigo-200 bg-white shadow-xl overflow-hidden max-h-[90vh] flex flex-col",
  panelWide:
    "w-full max-w-3xl rounded-xl border border-indigo-200 bg-white shadow-xl overflow-hidden max-h-[90vh] flex flex-col",
  head: `flex items-start justify-between gap-3 px-4 py-3 border-b shrink-0 ${BRAND_THEME.modalHead}`,
  title: `text-sm font-semibold ${BRAND_THEME.modalTitle}`,
  subtitle: "mt-0.5 text-[11px] text-slate-600",
  body: "p-4 overflow-y-auto flex-1",
  footer:
    "flex items-center justify-end gap-2 px-4 py-3 border-t border-indigo-100 bg-indigo-50/40 shrink-0",
  close:
    "inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-indigo-50 hover:text-indigo-700 transition-colors",
};
